import {
  Button,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  theme,
  Tree,
  Typography,
} from 'antd'
import type { InputRef, TreeDataNode } from 'antd'
import {
  isValidElement,
  Key,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { DownOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import {
  CustomMenuItemType,
  CustomSubMenuType,
} from './dynamic-menu/custom-menu-items-type'
import {
  isMenuDividerType,
  isGroupMenuType,
  isSubMenuType,
} from './dynamic-menu/helper/custom-menu-item-type-guard'
import IconGlobalApp from '../../../utils/IconGlobalApp'
import { mapCommonIcons } from '../../../utils/mapCommonIcons'

const { Text } = Typography

// Lấy string sau 1 search string
const getStringAfter = (mainString: string, searchString: string) => {
  const index = mainString.indexOf(searchString)
  if (index !== -1) {
    return mainString.substring(index + searchString.length)
  }
  return '' // Return an empty string if the searchString is not found
}

// Lấy children nếu là string thuần từ label: ReactNode
const convertLabelToString = (label: ReactNode) => {
  if (!isValidElement(label)) return label?.toString() || ''
  const children = label?.props?.children
  if (!children || typeof children !== 'string') return label?.toString() || ''

  return children
}

type MapResult = { data: TreeDataNode[]; expandKeys: string[] }

// Nest tất cả item con thành tree data
const mapNestTreeData = (
  items:
    | CustomSubMenuType['children']
    | CustomMenuItemType<'group'>['children'],
  parentKeys: string[]
): MapResult => {
  const result: MapResult = { data: [], expandKeys: [] }

  if (!items || items.length <= 0) return result

  items.forEach((item) => {
    if (!item || isMenuDividerType(item)) return
    const title = convertLabelToString(item.label)
    const key = `${parentKeys.join('/')}:label/${convertLabelToString(
      item.label
    )}:value${item?.key}`

    const haveChildren =
      (isSubMenuType(item) || isGroupMenuType(item)) && item.children

    const child = haveChildren
      ? mapNestTreeData(
          item.children,
          item?.key ? [...parentKeys, item.key.toString()] : parentKeys
        )
      : { data: [], expandKeys: [] }

    result.data.push({
      title,
      key,
      icon:
        !isMenuDividerType(item) && !isGroupMenuType(item) && item.icon ? (
          <span style={{ marginRight: 8 }}>{item.icon}</span>
        ) : undefined,
      selectable: !isSubMenuType(item) && !isGroupMenuType(item),
      disabled: isGroupMenuType(item),
      children: child.data,
    })

    result.expandKeys.push(key, ...child.expandKeys)
  })

  return result
}

// Tìm các item có label trùng với search
const searchItems = (
  query: string,
  data: CustomSubMenuType['children'] | CustomMenuItemType<'group'>['children'],
  parentKeys: string[]
): MapResult => {
  const result: MapResult = { data: [], expandKeys: [] }

  data?.forEach((item) => {
    if (!item || isMenuDividerType(item)) return
    const title = convertLabelToString(item.label)
    const key = `${parentKeys.join('/')}:label/${title}:value${item?.key}`

    const haveChildren =
      (isSubMenuType(item) || isGroupMenuType(item)) && item.children

    // Item cha
    const baseItem: TreeDataNode = {
      title,
      key,
      icon:
        !isMenuDividerType(item) && !isGroupMenuType(item) && item.icon ? (
          <span style={{ marginRight: 8 }}>{item.icon}</span>
        ) : undefined,
      selectable: !isSubMenuType(item) && !isGroupMenuType(item),
      disabled: isGroupMenuType(item),
    }

    // Logic check trùng search
    const isMatchWithSearch = title.toLowerCase().includes(query.toLowerCase())

    // Nếu item cha trùng thì expand tất cả item con
    if (isMatchWithSearch) {
      // Expand item con nếu có children
      const child = haveChildren
        ? mapNestTreeData(
            item.children,
            item?.key ? [...parentKeys, item.key.toString()] : parentKeys
          )
        : { data: [], expandKeys: [] }

      // Push vào kết quả
      result.data.push({
        ...baseItem,
        children: child.data,
      })
      result.expandKeys.push(key, ...child.expandKeys)

      // Nếu không trùng thì tìm tiếp xuống các item con
    } else if (
      // Nếu có children
      haveChildren
    ) {
      // Tìm trong các children
      const filteredChildren = searchItems(
        query,
        item.children,
        item?.key ? [...parentKeys, item.key.toString()] : parentKeys
      )
      // Nếu tìm thấy children trùng thì push vào kết quả
      if (filteredChildren.data.length > 0) {
        result.data.push({
          ...baseItem,
          children: filteredChildren.data,
        })
        result.expandKeys.push(key, ...filteredChildren.expandKeys)
      }
    }
  })

  // Trả về kết quả cuối cùng
  return result
}

interface IProps {
  menuItems: CustomSubMenuType[]
  onSelect: (_key: string) => void
}

const MenuSearch = ({ menuItems, onSelect }: IProps) => {
  const inputRef = useRef<InputRef | null>(null)
  const themeToken = theme.useToken().token

  const [form] = Form.useForm<{ search: string | null | undefined }>()
  const search = Form.useWatch('search', form)

  const [open, setOpen] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([])
  const [treeData, setTreeData] = useState<TreeDataNode[]>([])

  const onExpand = (newExpandedKeys: Key[]) => {
    setExpandedKeys(newExpandedKeys)
  }

  const onSelectItem = (selectedKeys: Key[]) => {
    // Xác định value trong key đầu
    const extractKey = selectedKeys?.[0]
      ? getStringAfter(selectedKeys[0].toString(), ':value')
      : null

    if (!extractKey) return

    onSelect(extractKey)
    setOpen(false)
  }

  const titleRender = (item: TreeDataNode) => {
    const strTitle = item.title as string
    const index = search
      ? strTitle.toLowerCase().indexOf(search.toLowerCase())
      : -1
    const beforeStr = strTitle.substring(0, index)
    const afterStr = strTitle.slice(index + (search?.length || 0))
    const matchedSubstring = strTitle.substring(
      index,
      index + (search?.length || 0)
    )

    return index > -1 ? (
      <Text style={{ fontSize: 16 }}>
        {beforeStr}
        <span
          style={{
            color: themeToken['blue-6'],
            fontWeight: themeToken.fontWeightStrong,
          }}
        >
          {matchedSubstring}
        </span>
        {afterStr}
      </Text>
    ) : (
      <Text style={{ fontSize: 16 }}>{strTitle}</Text>
    )
  }

  const debouncedSearch = debounce(
    () => {
      if (!search) {
        setTreeData([])
        setExpandedKeys([])
        return
      }
      const { data, expandKeys } = searchItems(search, menuItems, [])
      setTreeData(data)
      setExpandedKeys(expandKeys)
    },
    500,
    { leading: false, trailing: true }
  )

  useEffect(() => {
    debouncedSearch()

    return () => {
      debouncedSearch.cancel()
    }
  }, [search, menuItems])

  return (
    <>
      <Button
        icon={<IconGlobalApp name={mapCommonIcons.search} />}
        onClick={() => setOpen(true)}
        style={{ width: '100%', justifyContent: 'start' }}
      >
        Tìm kiếm
      </Button>

      <Modal
        title="Tìm kiếm"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={480}
        afterOpenChange={(isOpen) => {
          if (isOpen) {
            inputRef.current?.focus()
          } else {
            form.resetFields()
            setExpandedKeys([])
          }
        }}
      >
        <Flex vertical gap={24}>
          <Form name="searchMenu" form={form}>
            <Form.Item name="search" style={{ margin: 0 }}>
              <Input
                prefix={<IconGlobalApp name={mapCommonIcons.search} />}
                ref={inputRef}
                size="large"
                allowClear
                placeholder="Tìm kiếm"
                // stopPropagation để chặn bug không dùng được arrow key trong input trong modal trong menu.item
                // https://github.com/ant-design/ant-design/issues/34125
                onKeyDown={(e) => e.stopPropagation()}
              />
            </Form.Item>
          </Form>

          {treeData.length >= 1 ? (
            <Tree
              onExpand={onExpand}
              expandedKeys={expandedKeys}
              treeData={treeData}
              selectedKeys={[]}
              showLine
              showIcon
              switcherIcon={<DownOutlined />}
              style={{ maxHeight: '40vh', overflow: 'auto' }}
              onSelect={onSelectItem}
              titleRender={titleRender}
              defaultExpandAll
            />
          ) : (
            <Empty description="Nhập tên để tìm kiếm" />
          )}
        </Flex>
      </Modal>
    </>
  )
}

export default MenuSearch
